export const paths = {
  signin: "/signin",
  // Public - the customer clicks this from a service email; no login (Batch184).
  serviceFeedback: (token = ":token") => `/service-feedback/${token}`,
  dashboard: "/dashboard",
  changePassword: "/profile/change-password",
  users: "/admin/users",
  userDetail: (userId = ":userId") => `/admin/users/${userId}`,
  roles: "/admin/roles",
  designations: "/admin/designations",
  hrDepartments: "/masters/departments",
  hrDesignations: "/masters/designations",
  hrTax: "/masters/tax",
  hrDeductions: "/masters/deductions",
  hrHolidays: "/masters/holidays",
  hrAreas: "/masters/areas",
  // React-owned despite feeding a Flutter-only workflow (handoff §9.9,
  // Sales Enquiry service picker) - back-office masters administration,
  // same category as Departments/Area Master.
  serviceTypes: "/masters/service-types",
  // Technical Service Report checklist master (2026-09-02) - stays under HR
  // Masters in the nav, since it is master data configured once rather than a
  // day-to-day Technical activity.
  technicalChecklistItems: "/technical/checklist-items",
  // Technical Work (2026-09-08) - the manager-facing half of the Flutter
  // module: approve/push, assign, hold/resume, complete, verify & close and
  // read the full detail. Field activities (QR check-in, the service report
  // itself, product diagnosis) stay Flutter-only by design.
  technicalWork: "/technical/work",
  technicalWorkDetail: (recordGuid = ":recordGuid") => `/technical/work/${recordGuid}`,
  technicalPendingAssignment: "/technical/pending-assignment",
  technicalDutyList: "/technical/duty-list",
  // Technical Site Survey (Batch216, 2026-10-26) - office review + convert to quotation.
  technicalSurveys: "/technical/surveys",
  technicalSurveyDetail: (recordGuid = ":recordGuid") => `/technical/surveys/${recordGuid}`,
  // Alarm Response / Control Room (2026-09-17, Batch177)
  alarmBoard: "/alarms/board",
  alarmResponseDetail: (recordGuid = ":recordGuid") => `/alarms/responses/${recordGuid}`,
  alarmReport: "/alarms/report",
  alarmMobileUnits: "/alarms/mobile-units",
  customers: "/masters/customers",
  customerNew: "/masters/customers/new",
  // custId is only branch-local, not globally unique (1,297 of 3,695 collide
  // across branches) - every single-customer route needs branchId too.
  customerEdit: (branchId = ":branchId", custId = ":custId") =>
    `/masters/customers/${branchId}/${custId}/edit`,
  customerGuarding: (branchId = ":branchId", custId = ":custId") =>
    `/masters/customers/${branchId}/${custId}/guarding`,
  customerBulkGuards: (branchId = ":branchId", custId = ":custId") =>
    `/masters/customers/${branchId}/${custId}/bulk-guards`,
  customerFieldVisits: (branchId = ":branchId", custId = ":custId") =>
    `/masters/customers/${branchId}/${custId}/field-visits`,
  customerLocationsMap: "/masters/customers/locations-map",
  customerQrStickers: "/masters/customers/qr-stickers",
  products: "/masters/products",
  productGroups: "/masters/products/groups",
  productNew: "/masters/products/new",
  // Keyed by RecordGuid, not the ambiguous (branchId, productCode) business
  // key (switched server-side 2026-08-19, same identity-fix shape as
  // Employee's RecordGuid route, Batch48) - productCode isn't even
  // guaranteed unique within one branch, which threw a real 500 on Edit for
  // the handful of genuine duplicates.
  productEdit: (recordGuid = ":recordGuid") => `/masters/products/${recordGuid}/edit`,
  employees: "/masters/employees",
  employeeNew: "/masters/employees/new",
  // Keyed by RecordGuid, not empId (switched server-side 2026-08-12,
  // Batch48_EmployeeBranchScopeFix.sql) - empId turned out not to be unique
  // even within one branch at real data volume (e.g. BranchId=1/empId=1310
  // matches 5 different real people). RecordGuid is the only genuinely
  // unique key, same convention as Vehicles/Stock.
  employeeEdit: (recordGuid = ":recordGuid") => `/masters/employees/${recordGuid}/edit`,
  employeePromote: (recordGuid = ":recordGuid") => `/masters/employees/${recordGuid}/promote`,
  employeeRemarks: "/masters/employees/remarks",
  feedback: "/admin/feedback",
  settings: "/admin/settings",
  accessGuide: "/admin/access-guide",
  vehicles: "/masters/vehicles",
  vehicleNew: "/masters/vehicles/new",
  // vehicleId is globally unique (unlike custId/productCode) - no branchId
  // needed in the route, same as Employee's empId.
  vehicleEdit: (vehicleId = ":vehicleId") => `/masters/vehicles/${vehicleId}/edit`,

  // Stock Management - own top-level module (not nested under masters),
  // built against TexasAlarms.Api's src/Stock module. LPO/Stock-In/Stock-Out/
  // Return/Adjustment records are keyed by a globally-unique RecordGuid
  // (like Vehicles), so none of these routes need a {branchId} composite key.
  stockLpos: "/stock/lpos",
  // Filtered deep link into the LPO list. `status` accepts the real status
  // values and the pseudo-value "PENDING", which the list expands to
  // SUBMITTED + REVISED_SUBMITTED - the pair the dashboard's "Pending LPOs"
  // figure actually counts, and which no single dropdown value covers.
  stockLposFiltered: ({ status, dateFrom, dateTo } = {}) => {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (dateFrom) params.set("dateFrom", dateFrom);
    if (dateTo) params.set("dateTo", dateTo);
    const qs = params.toString();
    return qs ? `/stock/lpos?${qs}` : "/stock/lpos";
  },
  stockLpoNew: "/stock/lpos/new",
  stockLpoEdit: (recordGuid = ":recordGuid") => `/stock/lpos/${recordGuid}/edit`,
  stockLpoDetail: (recordGuid = ":recordGuid") => `/stock/lpos/${recordGuid}`,
  stockLpoPendingVehicleRequests: "/stock/lpos/pending-vehicle-requests",
  stockIn: "/stock/stock-in",
  stockInNew: "/stock/stock-in/new",
  stockInDetail: (recordGuid = ":recordGuid") => `/stock/stock-in/${recordGuid}`,
  stockOut: "/stock/stock-out",
  stockOutNew: "/stock/stock-out/new",
  stockOutDetail: (recordGuid = ":recordGuid") => `/stock/stock-out/${recordGuid}`,
  stockReturns: "/stock/returns",
  stockReturnNew: "/stock/returns/new",
  stockReturnDetail: (recordGuid = ":recordGuid") => `/stock/returns/${recordGuid}`,
  stockAdjustments: "/stock/adjustments",
  stockAdjustmentNew: "/stock/adjustments/new",
  stockAdjustmentDetail: (recordGuid = ":recordGuid") => `/stock/adjustments/${recordGuid}`,
  stockBalances: "/stock/balances",
  // Batch66 - a flat, drillable ledger unifying Stock-In/Out/Return/
  // Adjustment lines; no own detail route, "Document" links go to the
  // relevant one of the four existing detail routes above via recordGuid.
  stockMovementReport: "/stock/movement-report",

  // Petty Cash - own top-level module, against TexasAlarms.Api's src/
  // PettyCash module. Fund/Voucher/Refill/Reconciliation use the
  // master-detail split-view (like Employees/Customers/Products/Vehicles),
  // per direct instruction that this reads more naturally than Stock's
  // flat to-and-fro navigation for these four. Categories/Ledger/Reports
  // stay flat pages (no per-record workflow to show in a detail pane).
  // Self-Pay Ceiling has no route of its own - it's a Petty Cash-grouped
  // setting already editable on the existing paths.settings page.
  pettyCashFunds: "/pettycash/funds",
  pettyCashFundNew: "/pettycash/funds/new",
  pettyCashFundEdit: (fundId = ":fundId") => `/pettycash/funds/${fundId}/edit`,
  pettyCashVouchers: "/pettycash/vouchers",
  pettyCashVoucherNew: "/pettycash/vouchers/new",
  pettyCashVoucherDetail: (recordGuid = ":recordGuid") => `/pettycash/vouchers/${recordGuid}`,
  pettyCashRefills: "/pettycash/refills",
  pettyCashRefillNew: "/pettycash/refills/new",
  pettyCashRefillDetail: (recordGuid = ":recordGuid") => `/pettycash/refills/${recordGuid}`,
  pettyCashReconciliations: "/pettycash/reconciliations",
  pettyCashReconciliationNew: "/pettycash/reconciliations/new",
  pettyCashReconciliationDetail: (recordGuid = ":recordGuid") => `/pettycash/reconciliations/${recordGuid}`,
  pettyCashCategories: "/pettycash/categories",
  pettyCashLedger: "/pettycash/ledger",
  pettyCashReturns: "/pettycash/returns",
  pettyCashSummaryReport: "/pettycash/reports/summary",
  pettyCashVoucherRegister: "/pettycash/reports/register",
  pettyCashOverview: "/pettycash/reports/overview",

  // Sales Monitoring - own top-level module, against TexasAlarms.Api's
  // src/Sales module. Enquiries/Orders use the master-detail split-view
  // (per direct instruction), same shape as Petty Cash's Voucher/Refill
  // blocks.
  salesEnquiries: "/sales/enquiries",
  salesEnquiryNew: "/sales/enquiries/new",
  salesEnquiryDetail: (recordGuid = ":recordGuid") => `/sales/enquiries/${recordGuid}`,
  salesEnquiryResubmit: (recordGuid = ":recordGuid") => `/sales/enquiries/${recordGuid}/resubmit`,
  salesOrders: "/sales/orders",
  // Commission worklist (2026-09-08) - closed orders only, since a commission
  // is earned on completed business. Pure UI: the endpoints, the
  // Sales.ManageCommission permission and App_SalesOrderCommission all
  // already existed.
  salesCommissions: "/sales/commissions",
  salesOrderNewConfirmed: "/sales/orders/new-confirmed",
  salesOrderDetail: (recordGuid = ":recordGuid") => `/sales/orders/${recordGuid}`,
  salesDashboard: "/sales/dashboard",
  // Client Visit Report (Batch182). One flat report screen, not a split view:
  // a visit is read whole (remarks + checklist) in a modal, and there is
  // nothing to edit - visits are recorded in the field from the Flutter app.
  salesClientVisits: "/sales/client-visits",
  salesClientVisitMonthly: "/sales/client-visits/monthly-summary",

  // Quotations (Phase 1) - own module/DB/API against TexasAlarms.Api's
  // src/Quotations, listed under the Sales Monitoring nav section per direct
  // instruction but not an extension of Sales Enquiry/Order. Master-detail
  // split-view for both Prototypes and Quotations, same shape as Enquiries/
  // Orders above. Create is a modal (PrototypeCreateModal/QuotationCreateModal),
  // not a routed page - the Line Items table needs more horizontal room than
  // the split-view's detail pane gives it. Phase 2 (selecting an approved
  // Quotation from LPO/Stock-Out) is intentionally not wired up yet.
  quotationPrototypes: "/quotations/prototypes",
  quotationPrototypeDetail: (recordGuid = ":recordGuid") => `/quotations/prototypes/${recordGuid}`,
  quotationPrototypeEdit: (recordGuid = ":recordGuid") => `/quotations/prototypes/${recordGuid}/edit`,
  quotations: "/quotations",
  quotationDetail: (recordGuid = ":recordGuid") => `/quotations/${recordGuid}`,
  quotationEdit: (recordGuid = ":recordGuid") => `/quotations/${recordGuid}/edit`,

  // Guarding - own top-level module, Phase 1 (2026-09-02). Pending
  // Deployments surfaces Sales' own upcoming-deployments endpoint (fixed
  // server-side, Batch121); Supervisor Areas is brand new
  // (App_SupervisorAreaAssignment). Both master-detail split-views, per
  // direct instruction to use the split-view shape wherever it fits.
  guardingPendingDeployments: "/guarding/pending-deployments",
  // One order can be several pending requests (one per start date + service,
  // Batch161), so the detail link also names which one via ?date=&service=.
  guardingPendingDeploymentDetail: (recordGuid = ":recordGuid", request) => {
    const base = `/guarding/pending-deployments/${recordGuid}`;
    if (!request) return base;
    const qs = new URLSearchParams();
    if (request.date) qs.set("date", request.date);
    if (request.serviceTypeId != null) qs.set("service", String(request.serviceTypeId));
    const query = qs.toString();
    return query ? `${base}?${query}` : base;
  },
  guardingSupervisorAreas: "/guarding/supervisor-areas",
  guardingSupervisorAreaDetail: (employeeRecordGuid = ":employeeRecordGuid") =>
    `/guarding/supervisor-areas/${employeeRecordGuid}`,
  // React port of the Flutter Attendance module's Guard Lookup screen
  // (2026-09-05) - plain page, not a split-view, matching its own
  // search-then-browse shape rather than master-detail.
  guardingGuardLookup: "/guarding/guard-lookup",
  // Duty List (2026-09-14) - web version of the WinForms Duty List report
  // (frm_assignment_details); tabbed together with Guard Lookup.
  guardingDutyList: "/guarding/duty-list",
  // Attendance Dashboard (2026-09-09) - React port of the Flutter screen, under
  // Guarding beside Guard Lookup: "are my guards on post right now" is guarding
  // supervision, not HR attendance. Same Attendance.Mark|Attendance.View policy.
  guardingAttendanceDashboard: "/guarding/attendance-dashboard",
  // Office Attendance Posts (2026-09-15, Batch173) - manage the carrier slot each
  // office employee's kiosk fingerprint punch is credited to.
  guardingOfficeAttendancePosts: "/guarding/office-attendance-posts",

  // Leave Management (2026-09-05) - web port of the legacy frm_Leave/
  // Frm_Bulk_leave screens, merged into one page. Own top-level route, not
  // nested under masters (same reasoning as Guarding above).
  leaveManagement: "/leave",

  // Vehicle Maintenance Report/History (2026-09-03) - a brand new report,
  // no prior route to preserve, so it lives directly under the new Reports
  // section's own namespace rather than nested under /masters/vehicles.
  // Built entirely from existing endpoints (vehicles/requests, stock/lpos,
  // stock/movement-report, pettycash/vouchers) - no new API/DB.
  vehicleMaintenanceReport: "/reports/vehicles/maintenance",
  // Vehicle Expense Explorer (2026-10-29) - petty cash + maintenance + fuel per vehicle, keyword search across all.
  vehicleExpenseExplorer: "/reports/vehicles/expenses",
  // Fuel Management (2026-09-19) - against TexasAlarms.Api's src/Fuel. The
  // office half only: cards, card<->vehicle assignment, refills, and reading /
  // reviewing the purchases drivers record in the mobile app. Own /vehicles/fuel
  // namespace - the existing vehicle routes above keep their old paths.
  fuelCards: "/vehicles/fuel/cards",
  fuelVehicleSetup: "/vehicles/fuel/vehicle-setup",
  fuelRefills: "/vehicles/fuel/refills",
  fuelTransactions: "/vehicles/fuel/transactions",
  fuelExceptions: "/vehicles/fuel/exceptions",
  fuelReports: "/vehicles/fuel/reports",
  fuelMonthlySummary: "/vehicles/fuel/monthly-summary",
  // Technician Duty Report (2026-09-11, Batch159) - per-technician summary +
  // per-job detail, the web version of the legacy Daily Technical / Daily
  // Work Report prints.
  technicianDutyReport: "/reports/technical/duty",

  // AMC & Warranty (2026-09-07) - own top-level module per direct instruction, with
  // every AMC-related page under it. Equipment & Coverage is the register of what a
  // customer has plus the warranty on each item; AMC contracts join it here later.
  amcNew: "/amc/new",
  // Deep link to one contract on that same page. The AMC screen is deliberately a
  // single page whose contract, equipment and schedule panels open in place, so
  // there is no /amc/{guid} route to link to - the guid rides as a query param and
  // the page opens itself on that contract. Anything that knows a contract (the
  // dashboard cards) can therefore hand the user straight to it.
  amcRecord: (contractRecordGuid) => `/amc/new?contract=${contractRecordGuid}`,

  // Collections (2026-09-10) - read-only list. The module had an API slice and
  // dashboard widgets but no screen at all, which made it the one module in the
  // One Stop report whose rows had nowhere to drill to. Creating/editing a
  // collection stays Flutter-only (field collectors), so this is a landing and
  // lookup screen, not a CRUD module.
  collections: "/collections",
  // Collector customer lists + performance report (Batch189, 2026-09-21).
  collectorLists: "/collections/collector-lists",
  collectorPerformance: "/collections/collector-performance",
  collectorMonthlySummary: "/collections/collector-monthly-summary",

  // Filtered deep link into the Collections list, same idea as amcRecord above:
  // the dashboard knows the branch/customer/day behind a figure, so it can hand
  // the user a list already narrowed to it instead of an unfiltered screen they
  // have to re-filter by hand. Only params CollectionsListPage actually reads
  // are emitted (branchId, custId, dateFrom, dateTo, highlight) - adding others
  // here would silently do nothing.
  collectionsFiltered: ({ branchId, custId, custName, dateFrom, dateTo, highlight } = {}) => {
    const params = new URLSearchParams();
    if (branchId != null && branchId !== "") params.set("branchId", String(branchId));
    if (custId != null && custId !== "") params.set("custId", String(custId));
    // Display-only: the filter is by id, but without a name the Customer box
    // on the target page would sit empty and the narrowed list look unexplained.
    if (custId != null && custId !== "" && custName) params.set("custName", custName);
    if (dateFrom) params.set("dateFrom", dateFrom);
    if (dateTo) params.set("dateTo", dateTo);
    if (highlight) params.set("highlight", highlight);
    const qs = params.toString();
    return qs ? `/collections?${qs}` : "/collections";
  },

  // One Stop report (2026-09-10, Batch151) - one master record (Customer,
  // Employee, Product or Vehicle) + a date range, and every module's
  // transactions for it, grouped Branch -> Site -> Module -> Transaction type.
  // Drill-downs reuse the detail routes above; this report adds none of its own.
  oneStopReport: "/reports/one-stop",

  // Transaction Reconciliation (2026-09-16, Batch174) - every quotation item
  // traced Quotation -> LPO -> Stock-In -> Stock-Out, with a qty variance
  // (delivered vs quoted) and a value variance (margin).
  transactionReconciliationReport: "/reports/reconciliation",
};
